/**
 * Entrega de credencial: geração da senha e a mensagem pronta para o gestor
 * copiar e mandar (WhatsApp) a quem acabou de receber acesso.
 *
 * Vive em `shared` — e não no frontend — porque é o mesmo texto que o CLI de
 * provisionamento deve poder imprimir. Funções puras, sem IO.
 */

/**
 * Alfabeto SEM caracteres ambíguos: fora `0/O`, `1/l/I` e `5/S`. A senha é
 * digitada por uma pessoa num teclado de celular, muitas vezes lendo de outra
 * tela — cada par confundível vira um chamado de "não consigo entrar".
 */
const ALFABETO = "abcdefghijkmnpqrtuvwxyz2346789";

/** Grupos de 4 caracteres, separados por hífen — legível e fácil de ditar. */
const GRUPOS = 3;
const POR_GRUPO = 4;

/** Fonte de aleatoriedade (injetável nos testes; produção usa CSPRNG). */
export type FonteAleatoria = (n: number) => Uint8Array;

const aleatorioPadrao: FonteAleatoria = (n) => crypto.getRandomValues(new Uint8Array(n));

/**
 * Senha provisória no formato `caint-7hkm-2rqp`.
 *
 * Usa `crypto.getRandomValues` (CSPRNG), nunca `Math.random`. O módulo do byte
 * introduz um viés desprezível para este alfabeto (30 valores em 256), aceitável
 * para senha de primeiro acesso que se espera trocada.
 */
export function gerarSenhaProvisoria(aleatorio: FonteAleatoria = aleatorioPadrao): string {
  const bytes = aleatorio(GRUPOS * POR_GRUPO);
  const grupos: string[] = [];
  for (let g = 0; g < GRUPOS; g++) {
    let grupo = "";
    for (let i = 0; i < POR_GRUPO; i++) {
      const byte = bytes[g * POR_GRUPO + i] ?? 0;
      grupo += ALFABETO[byte % ALFABETO.length];
    }
    grupos.push(grupo);
  }
  return `caint-${grupos.join("-")}`;
}

export interface DadosAcesso {
  readonly nome: string;
  /** Login = e-mail. */
  readonly matricula: string;
  /**
   * Senha em claro. AUSENTE quando a mensagem é reenviada depois: o servidor só
   * guarda o hash, então não há como reexibir a original. Nesse caso o texto
   * orienta a usar a senha que a pessoa já tem, ou pedir uma nova.
   */
  readonly senha?: string;
  /** `unidade` recebe o Portal do Plano de Ação; os demais, o app principal. */
  readonly papel: string;
  /** Rótulo legível do perfil (ex.: "Supervisão AUDIN"). Resolvido por quem chama. */
  readonly perfil: string;
  /** Nome da unidade — só faz sentido para contas `unidade`. */
  readonly unidadeNome?: string;
  /** Origem do app, ex.: `https://caint.direcaoclinica.com.br` (sem barra final). */
  readonly origem: string;
  /** `true` quando é redefinição, não primeiro acesso — muda a abertura do texto. */
  readonly redefinicao?: boolean;
}

/** Primeiro nome, para a saudação não ficar formal demais no WhatsApp. */
function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

function semBarraFinal(origem: string): string {
  return origem.replace(/\/+$/, "");
}

/**
 * Mensagem pronta para copiar e colar. Duas formas, porque contas `unidade` são
 * BLOQUEADAS no app principal e só entram pelo portal `/unidade` — mandar o link
 * errado para uma unidade gera chamado na certa.
 *
 * O pedido de troca de senha é convenção, não trava: o sistema ainda não força a
 * troca no primeiro acesso. Enquanto não forçar, a senha enviada continua valendo
 * — daí o aviso de não repassar.
 */
export function mensagemAcesso(d: DadosAcesso): string {
  const base = semBarraFinal(d.origem);
  const ehUnidade = d.papel === "unidade";
  // MESMO endereço para todos: o login é único e a própria tela detecta o perfil
  // — a rota `/unidade` deixou de existir. Mandar a unidade para lá sugeria um
  // portal separado que não há mais (corrigido em 24/ago/2026).
  const link = base;

  // Três aberturas: acesso novo, senha redefinida e reenvio das instruções (sem
  // senha). Reenvio não pode dizer "está pronto" — soaria como conta nova.
  const situacao = d.senha === undefined ? "reenvio" : d.redefinicao ? "redefinida" : "nova";
  const aberturaInterna = {
    nova: `Olá, ${primeiroNome(d.nome)}! Seu acesso ao sistema CAINT está pronto.`,
    redefinida: `Olá, ${primeiroNome(d.nome)}! Sua senha do sistema CAINT foi redefinida.`,
    reenvio: `Olá, ${primeiroNome(d.nome)}! Segue como acessar o sistema CAINT.`,
  }[situacao];
  const alvo = d.unidadeNome ? `do ${d.unidadeNome} ` : "";
  const aberturaUnidade = {
    nova: `Olá! O acesso ${alvo}ao Portal do Plano de Ação da CAINT está pronto.`,
    redefinida: `Olá! O acesso ${alvo}ao Portal do Plano de Ação da CAINT foi redefinido.`,
    reenvio: `Olá! Segue como acessar o Portal do Plano de Ação ${alvo}da CAINT.`,
  }[situacao];
  const abertura = ehUnidade ? aberturaUnidade : aberturaInterna;

  const proposito = ehUnidade
    ? "Neste portal a unidade responde os itens do Plano de Ação (FACR) apontados na avaliação."
    : `Seu perfil: ${d.perfil}.`;

  const linhas = [
    abertura,
    "",
    `🔗 ${link}`,
    `👤 Usuário: ${d.matricula}`,
    ...(d.senha
      ? [`🔑 Senha: ${d.senha}`]
      : ["🔑 Senha: a que você já usa. Se não lembrar, me avise que eu gero uma nova."]),
    "",
    proposito,
    "",
    d.senha
      ? "⚠️ Troque a senha assim que entrar e não repasse esta mensagem."
      : "⚠️ Não repasse esta mensagem.",
  ];

  if (!ehUnidade) {
    linhas.push(
      "",
      'Dica: abra no Chrome ou Safari e use "Adicionar à tela de início" para usar como aplicativo, inclusive sem internet.',
    );
  }
  return linhas.join("\n");
}
